import React, { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { INFRACTION_TYPES } from '../lib/constants'
import { ROLES } from '../lib/roles'
import { T, card, cardTitle, tbl, th, td, label, btnPrimary, alert as ALT } from '../lib/theme'

const LEVELS = [
  'Critical',
  'Further Investigation',
  'Warning',
]

const STATUS_META = {
  reported:        { label:'Reported',         color:'#8b949e', bg:'rgba(139,148,158,.12)', border:'rgba(139,148,158,.3)' },
  under_review:    { label:'Under Review',     color:'#E8A020', bg:'rgba(232,160,32,.15)',  border:'rgba(232,160,32,.3)'  },
  pending_admin:   { label:'Pending Approval', color:'#1E90FF', bg:'rgba(30,144,255,.12)',  border:'rgba(30,144,255,.3)'  },
  pending_payment: { label:'Pending Payment',  color:'#f0883e', bg:'rgba(240,136,62,.12)',  border:'rgba(240,136,62,.3)'  },
  closed:          { label:'Closed',           color:'#3fb950', bg:'rgba(63,185,80,.12)',   border:'rgba(63,185,80,.3)'   },
}

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.reported
  return (
    <span style={{ display:'inline-block', padding:'2px 8px', borderRadius:20, fontSize:9,
      fontWeight:700, color:m.color, background:m.bg, border:`1px solid ${m.border}`,
      whiteSpace:'nowrap' }}>
      {m.label}
    </span>
  )
}

export default function Infractions({ session, roleCtx }) {
  const role      = roleCtx?.role || 'guest'
  const canLog    = role === ROLES.ADMIN || role === ROLES.ENFORCEMENT
  const userEmail = session?.user?.email || ''
  const userName  = session?.user?.user_metadata?.full_name || userEmail

  const [superAgents,  setSuperAgents]  = useState([])
  const [infractions,  setInfractions]  = useState([])
  const [enforcers,    setEnforcers]    = useState([])
  const [loading,      setLoading]      = useState(true)
  const [saving,       setSaving]       = useState(false)
  const [msg,          setMsg]          = useState(null)
  const [form,         setForm]         = useState({
    super_agent_id: '',
    date_logged:    '',
    infraction_types: [],
    description:    '',
    notes:          '',
    logged_by:      role === ROLES.ENFORCEMENT ? userName : '',
    enforcement_level: '',
  })

  async function load() {
    const [{ data: sa }, { data: inf }, { data: eu }] = await Promise.all([
      supabase.from('super_agents').select('id,company_name,owner_name,lgas(name)').eq('status','Active').order('company_name'),
      supabase.from('infractions')
        .select('*,super_agents(company_name,owner_name,lgas(name))')
        .order('date_logged', { ascending: false })
        .limit(300),
      supabase.from('user_roles').select('email,display_name').eq('role','enforcement'),
    ])
    setSuperAgents(sa || [])
    setInfractions(inf || [])
    setEnforcers(eu || [])
    setLoading(false)
  }

  useEffect(() => {
    load()
    if (role === ROLES.ENFORCEMENT) {
      setForm(f => ({ ...f, logged_by: userName }))
    }
  }, [role, userName])

  function setF(k, v) { setForm(f => ({ ...f, [k]: v })) }
  function toggleType(t) {
    setForm(f => ({
      ...f,
      infraction_types: f.infraction_types.includes(t)
        ? f.infraction_types.filter(x => x !== t)
        : [...f.infraction_types, t],
    }))
  }

  async function save() {
    if (!form.super_agent_id || !form.date_logged || form.infraction_types.length === 0) {
      setMsg({ type:'err', text:'Super Agent, date, and at least one infraction type are required.' })
      return
    }
    setSaving(true); setMsg(null)
    const payload = {
      super_agent_id:    form.super_agent_id,
      date_logged:       form.date_logged,
      infraction_types:  form.infraction_types,
      description:       form.description,
      notes:             form.notes,
      logged_by:         form.logged_by,
      enforcement_level: form.enforcement_level,
      resolution_status: 'reported',
    }
    const { data: newInf, error } = await supabase
      .from('infractions').insert([payload]).select().single()
    if (error) { setMsg({ type:'err', text: error.message }); setSaving(false); return }

    // Log the initial history entry
    await supabase.from('infraction_status_history').insert([{
      infraction_id: newInf.id,
      from_status:   null,
      to_status:     'reported',
      actor_name:    form.logged_by || userName,
      actor_role:    role,
      note:          'Infraction logged by enforcement officer',
    }])

    setMsg({ type:'ok', text:'Infraction logged successfully.' })
    setForm({
      super_agent_id: '', date_logged: '', infraction_types: [],
      description: '', notes: '',
      logged_by: role === ROLES.ENFORCEMENT ? userName : '',
      enforcement_level: '',
    })
    setSaving(false)
    load()
  }

  const selStyle = {
    width:'100%', padding:'9px 12px', background:T.surface3, border:`1px solid ${T.border}`,
    borderRadius:6, fontSize:12, fontFamily:T.font, color:T.txtPrimary, outline:'none', marginBottom:14,
  }

  if (loading) return <div style={{ color:T.txtSecondary, fontSize:13 }}>Loading…</div>

  return (
    <div>
      <div style={{ marginBottom:24 }}>
        <div style={{ fontSize:20, fontWeight:800, color:T.txtPrimary, marginBottom:4 }}>Infractions</div>
        <div style={{ fontSize:12, color:T.txtSecondary }}>
          Log and review Super Agent infractions · Track resolution status
        </div>
      </div>

      {!canLog && (
        <div style={{ ...ALT.info, marginBottom:16 }}>
          You have view-only access to infractions. Contact an Admin or Enforcement user to log a new infraction.
        </div>
      )}

      <div style={{ display:'flex', flexDirection:'column', gap:16 }}>

        {/* ── Log form (admin / enforcement only) ── */}
        {canLog && (
          <div style={card}>
            <div style={cardTitle}>Log New Infraction</div>
            {msg && <div style={ALT[msg.type] || ALT.info}>{msg.text}</div>}

            {/* Row 1: Super Agent + Date + Severity */}
            <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr', gap:12, marginBottom:14 }}>
              <div>
                <label style={{ fontSize:10, fontWeight:600, color:T.txtMuted, display:'block',
                  letterSpacing:'.06em', textTransform:'uppercase', marginBottom:4 }}>SUPER AGENT *</label>
                <select style={{ ...selStyle, marginBottom:0 }} value={form.super_agent_id}
                  onChange={e => setF('super_agent_id', e.target.value)}>
                  <option value="">-- Select Super Agent --</option>
                  {superAgents.map(sa => (
                    <option key={sa.id} value={sa.id}>
                      {sa.company_name} — {sa.owner_name} ({sa.lgas?.name})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ fontSize:10, fontWeight:600, color:T.txtMuted, display:'block',
                  letterSpacing:'.06em', textTransform:'uppercase', marginBottom:4 }}>DATE *</label>
                <input style={{ ...selStyle, marginBottom:0 }} type="date" value={form.date_logged}
                  onChange={e => setF('date_logged', e.target.value)} />
              </div>
              <div>
                <label style={{ fontSize:10, fontWeight:600, color:T.txtMuted, display:'block',
                  letterSpacing:'.06em', textTransform:'uppercase', marginBottom:4 }}>SEVERITY LEVEL</label>
                <select style={{ ...selStyle, marginBottom:0 }} value={form.enforcement_level}
                  onChange={e => setF('enforcement_level', e.target.value)}>
                  <option value="">-- Select Level --</option>
                  {LEVELS.map(l => <option key={l}>{l}</option>)}
                </select>
              </div>
            </div>

            {/* Row 2: Infraction types + Description + Notes side by side */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12, marginBottom:14 }}>
              <div>
                <label style={{ fontSize:10, fontWeight:600, color:T.txtMuted, display:'block',
                  letterSpacing:'.06em', textTransform:'uppercase', marginBottom:4 }}>
                  INFRACTION TYPES * (select all that apply)
                </label>
                <div style={{ border:`1px solid ${T.border}`, borderRadius:8, padding:'10px 14px',
                  height:140, overflowY:'auto', background:T.surface3 }}>
                  {INFRACTION_TYPES.map(t => (
                    <label key={t} style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8,
                      fontSize:12, cursor:'pointer',
                      color: form.infraction_types.includes(t) ? T.txtPrimary : T.txtSecondary }}>
                      <input type="checkbox" checked={form.infraction_types.includes(t)}
                        onChange={() => toggleType(t)} style={{ accentColor:T.blue }} />
                      {t}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label style={{ fontSize:10, fontWeight:600, color:T.txtMuted, display:'block',
                  letterSpacing:'.06em', textTransform:'uppercase', marginBottom:4 }}>
                  DESCRIPTION / NOTES
                </label>
                <textarea style={{ ...selStyle, height:140, resize:'none', marginBottom:0 }}
                  value={form.description} onChange={e => setF('description', e.target.value)}
                  placeholder="Describe the infraction…" maxLength={300} />
                <div style={{ fontSize:10, color:T.txtMuted, textAlign:'right', marginTop:2 }}>
                  {form.description.length}/300
                </div>
              </div>
              <div>
                <label style={{ fontSize:10, fontWeight:600, color:T.txtMuted, display:'block',
                  letterSpacing:'.06em', textTransform:'uppercase', marginBottom:4 }}>
                  ADDITIONAL NOTES
                </label>
                <textarea style={{ ...selStyle, height:140, resize:'none', marginBottom:0 }}
                  value={form.notes} onChange={e => setF('notes', e.target.value)}
                  placeholder="Field observations, evidence, follow-up…" maxLength={200} />
                <div style={{ fontSize:10, color:T.txtMuted, textAlign:'right', marginTop:2 }}>
                  {form.notes.length}/200
                </div>
              </div>
            </div>

            {/* Row 3: Logged By + Submit */}
            <div style={{ display:'flex', gap:12, alignItems:'flex-end' }}>
              <div style={{ flex:1 }}>
                <label style={{ fontSize:10, fontWeight:600, color:T.txtMuted, display:'block',
                  letterSpacing:'.06em', textTransform:'uppercase', marginBottom:4 }}>LOGGED BY</label>
                {role === ROLES.ENFORCEMENT ? (
                  <div style={{ ...selStyle, marginBottom:0, color:T.txtSecondary, cursor:'default',
                    userSelect:'none', background:T.surface2, display:'flex', alignItems:'center', gap:6 }}>
                    <span style={{ fontSize:11 }}>🔒</span>
                    {userName}
                  </div>
                ) : (
                  <select style={{ ...selStyle, marginBottom:0 }} value={form.logged_by}
                    onChange={e => setF('logged_by', e.target.value)}>
                    <option value="">-- Select Enforcement User --</option>
                    {enforcers.map(u => (
                      <option key={u.email} value={u.display_name || u.email}>{u.display_name || u.email}</option>
                    ))}
                  </select>
                )}
              </div>
              <button style={{ ...btnPrimary, minWidth:140, height:38 }}
                onClick={save} disabled={saving}>
                {saving ? 'Saving…' : 'Log Infraction'}
              </button>
            </div>
          </div>
        )}

        {/* ── Infraction log ── */}
        <div style={card}>
          <div style={cardTitle}>Infraction Log ({infractions.length} records)</div>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
              <thead>
                <tr>
                  {['Date','Super Agent','LGA','Infractions','Description','Level','Status','Logged By'].map(h => (
                    <th key={h} style={{ background:T.surface2, color:T.txtSecondary, fontWeight:600,
                      padding:'9px 12px', textAlign:'left', fontSize:10, textTransform:'uppercase',
                      letterSpacing:'.06em', borderBottom:`1px solid ${T.border}`, whiteSpace:'nowrap' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {infractions.map(inf => (
                  <tr key={inf.id}>
                    <td style={{ ...td, color:T.txtSecondary, whiteSpace:'nowrap' }}>{inf.date_logged}</td>
                    <td style={{ ...td, fontWeight:600 }}>
                      {inf.super_agents?.company_name || '—'}
                      {inf.super_agents?.owner_name && (
                        <div style={{ fontSize:10, color:T.txtMuted, fontWeight:400 }}>
                          {inf.super_agents.owner_name}
                        </div>
                      )}
                    </td>
                    <td style={{ ...td, color:T.txtSecondary }}>{inf.super_agents?.lgas?.name || '—'}</td>
                    <td style={{ ...td, minWidth:100, maxWidth:160 }}>
                      <div style={{ display:'flex', flexWrap:'wrap', gap:3 }}>
                        {(inf.infraction_types || []).map(t => (
                          <span key={t} style={{ display:'inline-block', padding:'1px 5px', borderRadius:10,
                            fontSize:8, fontWeight:600, lineHeight:'16px',
                            background:'rgba(248,81,73,.1)', color:'#f85149',
                            border:'1px solid rgba(248,81,73,.18)', whiteSpace:'nowrap' }}>
                            {t}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td style={{ ...td, minWidth:200, maxWidth:320, color:T.txtSecondary }}>
                      {inf.description || inf.notes ? (
                        <span style={{ fontSize:11 }} title={inf.description || inf.notes}>
                          {(inf.description || inf.notes || '').length > 120
                            ? (inf.description || inf.notes).slice(0, 120) + '…'
                            : (inf.description || inf.notes)}
                        </span>
                      ) : <span style={{ color:T.txtMuted }}>—</span>}
                    </td>
                    <td style={{ ...td, fontSize:10, fontWeight:600, color:'#f0883e', width:90, whiteSpace:'nowrap' }}>
                      {inf.enforcement_level || '—'}
                    </td>
                    <td style={{ ...td, whiteSpace:'nowrap' }}>
                      <StatusBadge status={inf.resolution_status || 'reported'} />
                    </td>
                    <td style={{ ...td, fontSize:10, color:T.txtMuted }}>{inf.logged_by || '—'}</td>
                  </tr>
                ))}
                {infractions.length === 0 && (
                  <tr><td colSpan={8} style={{ ...td, textAlign:'center', color:T.txtMuted }}>
                    No infractions logged yet
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}
